import {
  ArrowDownToLine,
  Ban,
  CheckCircle2,
  RotateCw,
  Search,
  Trash2,
  TriangleAlert,
  WifiOff,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { ApiError } from '@/api/client'
import { PageHeader, pageClassName } from '@/components/ui/Page'
import { ErrorNotice } from '@/components/ui/Feedback'
import { useCancelJob, useProviders, useJobs, useRetryAllJobs, useRetryJob } from '@/api/hooks'
import type { DownloadJob, JobStatus } from '@/api/types'
import { Cover } from '@/components/Cover'
import { Button, EmptyState, IconButton, ProgressBar, Skeleton, toast } from '@/components/ui'
import { cn } from '@/lib/cn'
import { formatDate, formatEta, formatSpeed, stageLabel } from '@/lib/format'

import { ClearHistoryDialog } from './ClearHistoryDialog'

function isActive(job: DownloadJob): boolean {
  return job.status === 'queued' || job.status === 'running'
}

/** Finished jobs are grouped and stamped by when they ended, not when they began. */
function historyTimestamp(job: DownloadJob): string {
  return job.finished_at ?? job.started_at ?? job.created_at
}

function dayKey(iso: string): string {
  const date = new Date(iso)
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
}

function formatClock(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

function messageFor(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback
}

// ---------------------------------------------------------------------------
// Live-update health
// ---------------------------------------------------------------------------

type LiveStatus = 'live' | 'stale' | 'offline'

const STALE_AFTER_MS = 15_000

/**
 * Whether progress is actually reaching this page.
 *
 * AppShell owns the single job EventSource for the whole app, and a page must
 * not open a second one, so the socket's state is not directly observable here.
 * What is observable is the query cache: both the SSE merge and the 5s polling
 * fallback bump `dataUpdatedAt`. If work is in flight and nothing has landed for
 * STALE_AFTER_MS, neither channel is delivering and the numbers on screen are
 * older than they look.
 */
function useLiveStatus(hasActiveJobs: boolean, dataUpdatedAt: number): LiveStatus {
  const [online, setOnline] = useState(() => navigator.onLine)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const sync = () => setOnline(navigator.onLine)
    window.addEventListener('online', sync)
    window.addEventListener('offline', sync)
    return () => {
      window.removeEventListener('online', sync)
      window.removeEventListener('offline', sync)
    }
  }, [])

  useEffect(() => {
    if (!hasActiveJobs) return
    setNow(Date.now())
    const timer = window.setInterval(() => setNow(Date.now()), 5_000)
    return () => window.clearInterval(timer)
  }, [hasActiveJobs])

  if (!online) return 'offline'
  if (hasActiveJobs && dataUpdatedAt > 0 && now - dataUpdatedAt > STALE_AFTER_MS) return 'stale'
  return 'live'
}

function ConnectionNotice({ status }: { status: LiveStatus }) {
  if (status === 'live') return null

  return (
    <div
      role="status"
      className="mt-3 flex items-start gap-2 rounded-lg bg-surface-sunken px-3 py-2 text-xs text-text-muted"
    >
      <WifiOff className="mt-px h-4 w-4 shrink-0 text-text-subtle" aria-hidden="true" />
      <p>
        {status === 'offline'
          ? 'You are offline. Downloads keep running on the server — this list will catch up when the connection returns.'
          : 'Live updates are not coming through right now. Progress below may be a few seconds behind.'}
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Job card
// ---------------------------------------------------------------------------

const STATUS_TEXT: Record<JobStatus, string> = {
  queued: 'Waiting to start',
  running: 'Downloading',
  succeeded: 'Added to your library',
  failed: 'Download failed',
  cancelled: 'Cancelled',
}

function StatusIcon({ status }: { status: JobStatus }) {
  if (status === 'succeeded') {
    return <CheckCircle2 className="h-3.5 w-3.5 text-success" aria-hidden="true" />
  }
  if (status === 'failed') {
    return <TriangleAlert className="h-3.5 w-3.5 text-danger" aria-hidden="true" />
  }
  if (status === 'cancelled') {
    return <Ban className="h-3.5 w-3.5 text-text-subtle" aria-hidden="true" />
  }
  return null
}

function Dot() {
  return (
    <span aria-hidden="true" className="text-text-subtle">
      ·
    </span>
  )
}

function JobCard({ job }: { job: DownloadJob }) {
  const { data: providers = [] } = useProviders()
  const cancel = useCancelJob()
  const retry = useRetryJob()

  const active = isActive(job)
  const percent = Math.max(0, Math.min(100, Math.round(job.progress)))
  const speed = active ? formatSpeed(job.speed_bps) : ''
  const eta = active ? formatEta(job.eta_seconds) : ''
  const finishedAt = formatClock(job.finished_at)

  return (
    <li className="rounded-card border border-border bg-surface-raised p-3">
      <div className="flex items-start gap-3">
        <Cover src={job.track.artwork_url} size="md" />

        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-text">{job.track.title}</p>
              <p className="truncate text-xs text-text-muted">{job.track.artist_name}</p>
            </div>

            {/* Same endpoint either way: stop it if it is running, otherwise take
                it out of the list. The label has to say which, though. */}
            <IconButton
              aria-label={
                active
                  ? `Cancel download of ${job.track.title}`
                  : `Remove ${job.track.title} from download history`
              }
              size="sm"
              loading={cancel.isPending}
              onClick={() => cancel.mutate(job.id)}
              className="-mt-1"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </IconButton>
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-muted">
            <span className="rounded-full bg-surface-hover px-2 py-0.5 font-medium text-text-subtle">
              {providers.find((provider) => provider.slug === job.provider)?.name ?? job.provider}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <StatusIcon status={job.status} />
              {active ? stageLabel(job.stage) : STATUS_TEXT[job.status]}
            </span>
            {!active && finishedAt ? (
              <>
                <Dot />
                <span className="tabular-nums">{finishedAt}</span>
              </>
            ) : null}
            {job.status === 'failed' && job.attempts > 1 ? (
              <>
                <Dot />
                <span>{job.attempts} attempts</span>
              </>
            ) : null}
          </div>

          {active ? (
            <div className="mt-2.5">
              <ProgressBar
                value={percent}
                indeterminate={job.status === 'queued'}
                label={`Download progress for ${job.track.title}`}
              />
              <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-subtle">
                {job.status === 'running' ? <span className="tabular-nums">{percent}%</span> : null}
                {speed ? (
                  <>
                    {job.status === 'running' ? <Dot /> : null}
                    <span className="tabular-nums">{speed}</span>
                  </>
                ) : null}
                {eta ? (
                  <>
                    <Dot />
                    <span className="tabular-nums">{eta}</span>
                  </>
                ) : null}
              </div>
            </div>
          ) : null}

          {job.status === 'failed' ? (
            <div className="mt-2.5 rounded-lg bg-surface-sunken px-3 py-2.5">
              <p className="text-xs text-danger">
                {job.error_message ||
                  'The download stopped before the file was finished, and the server did not say why.'}
              </p>

              {job.retryable ? (
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-2"
                  loading={retry.isPending}
                  onClick={() => retry.mutate(job.id)}
                >
                  <RotateCw className="h-4 w-4" aria-hidden="true" />
                  Retry
                </Button>
              ) : (
                <p className="mt-1.5 text-xs text-text-muted">
                  Retrying will not help with this one. Try adding the song again from search — a
                  different source may work.
                </p>
              )}
            </div>
          ) : null}

          {cancel.isError ? (
            <p className="mt-2 text-xs text-danger">
              {messageFor(cancel.error, 'Could not cancel this download. It may have just finished.')}
            </p>
          ) : null}
          {retry.isError ? (
            <p className="mt-2 text-xs text-danger">
              {messageFor(retry.error, 'Could not start the retry. Please try again in a moment.')}
            </p>
          ) : null}
        </div>
      </div>
    </li>
  )
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function JobCardSkeleton() {
  return (
    <li className="rounded-card border border-border bg-surface-raised p-3">
      <div className="flex items-start gap-3">
        <Skeleton className="h-12 w-12 shrink-0 rounded-md" />
        <div className="flex min-w-0 flex-1 flex-col gap-2 py-0.5">
          <Skeleton className="h-3.5 w-1/2 max-w-56" />
          <Skeleton className="h-3 w-1/3 max-w-40" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
    </li>
  )
}

function JobListSkeleton() {
  return (
    <div role="status" aria-label="Loading downloads" className="mt-6">
      <Skeleton className="mb-3 h-3.5 w-28" />
      <ul className="flex flex-col gap-2">
        {Array.from({ length: 5 }, (_, index) => (
          <JobCardSkeleton key={index} />
        ))}
      </ul>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

interface DayGroup {
  key: string
  label: string
  jobs: DownloadJob[]
}

const STATUS_ORDER: Record<JobStatus, number> = {
  running: 0,
  queued: 1,
  failed: 2,
  succeeded: 3,
  cancelled: 4,
}

export function DownloadsPage() {
  const navigate = useNavigate()
  const { data, isPending, isError, error, refetch, isFetching, dataUpdatedAt } = useJobs('all')

  const jobs = useMemo(() => data?.results ?? [], [data])

  const { active, history } = useMemo(() => {
    const activeJobs = jobs
      .filter(isActive)
      .sort(
        (a, b) =>
          STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
          Date.parse(a.created_at) - Date.parse(b.created_at),
      )

    const finished = jobs
      .filter((job) => !isActive(job))
      .sort((a, b) => Date.parse(historyTimestamp(b)) - Date.parse(historyTimestamp(a)))

    const groups: DayGroup[] = []
    for (const job of finished) {
      const timestamp = historyTimestamp(job)
      const key = dayKey(timestamp)
      const last = groups.at(-1)
      if (last && last.key === key) {
        last.jobs.push(job)
      } else {
        groups.push({ key, label: formatDate(timestamp), jobs: [job] })
      }
    }

    return { active: activeJobs, history: groups }
  }, [jobs])

  const liveStatus = useLiveStatus(active.length > 0, dataUpdatedAt)

  const [clearOpen, setClearOpen] = useState(false)
  const retryAll = useRetryAllJobs()
  const historyCount = useMemo(
    () => history.reduce((total, group) => total + group.jobs.length, 0),
    [history],
  )
  const failedCount = useMemo(
    () => jobs.filter((job) => job.status === 'failed').length,
    [jobs],
  )

  return (
    <div className={pageClassName('browse')}>
      <PageHeader
        title="Downloads"
        subtitle={<>
          {active.length > 0 ? `${active.length} in progress` : 'Everything you have added, newest first.'}
          <ConnectionNotice status={liveStatus} />
        </>}
        actions={
          <div className="flex shrink-0 items-center gap-2">
            {/* Failures almost always share one cause — an expired provider
                session, a mount that vanished — so once it is fixed the next
                action is "all of those again", not one click per row. */}
            {failedCount > 0 ? (
              <Button
                variant="secondary"
                size="sm"
                loading={retryAll.isPending}
                onClick={() =>
                  retryAll.mutate(undefined, {
                    onSuccess: (result) =>
                      toast.success(
                        result.requeued > 0
                          ? `Retrying ${result.requeued} ${result.requeued === 1 ? 'download' : 'downloads'}`
                          : 'Nothing to retry',
                        result.skipped > 0
                          ? `${result.skipped} cannot be retried and ${result.skipped === 1 ? 'was' : 'were'} skipped.`
                          : undefined,
                      ),
                    onError: () =>
                      toast.error('Could not start the retries', 'Try again in a moment.'),
                  })
                }
              >
                <RotateCw className="h-4 w-4" aria-hidden="true" />
                Retry all
                <span className="sr-only"> {failedCount} failed downloads</span>
              </Button>
            ) : null}

            {historyCount > 0 ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setClearOpen(true)}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                <span className="hidden sm:inline">Clear history</span>
                <span className="sr-only sm:hidden">Clear history</span>
              </Button>
            ) : null}
          </div>
        }
      />

      <ClearHistoryDialog
        open={clearOpen}
        onOpenChange={setClearOpen}
        historyCount={historyCount}
        failedCount={failedCount}
        activeCount={active.length}
      />

      {isPending ? <JobListSkeleton /> : null}

      {isError && jobs.length === 0 ? (
        <ErrorNotice busy={isFetching} onRetry={() => void refetch()}>
          {messageFor(error, 'The download list could not be loaded.')}
        </ErrorNotice>
      ) : null}

      {!isPending && !isError && jobs.length === 0 ? (
        <EmptyState
          icon={ArrowDownToLine}
          title="No downloads yet"
            description="Downloads for library songs and songs you play appear here."
          action={{
            label: 'Find something to play',
            onClick: () => void navigate('/search'),
            icon: Search,
          }}
        />
      ) : null}

      {active.length > 0 ? (
        <section className="mt-6" aria-labelledby="downloads-active">
          <h2
            id="downloads-active"
            className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-subtle"
          >
            In progress
          </h2>
          <ul className="flex flex-col gap-2">
            {active.map((job) => (
              <JobCard key={job.id} job={job} />
            ))}
          </ul>
        </section>
      ) : null}

      {history.map((group, index) => (
        <section
          key={group.key}
          className={cn(index === 0 && active.length === 0 ? 'mt-6' : 'mt-8')}
          aria-labelledby={`downloads-day-${group.key}`}
        >
          <h2
            id={`downloads-day-${group.key}`}
            className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-subtle"
          >
            {group.label}
          </h2>
          <ul className="flex flex-col gap-2">
            {group.jobs.map((job) => (
              <JobCard key={job.id} job={job} />
            ))}
          </ul>
        </section>
      ))}

      {data?.next ? (
        <p className="mt-6 text-center text-xs text-text-subtle">
          Showing the most recent downloads. Older ones are kept on the server.
        </p>
      ) : null}
    </div>
  )
}
